import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Download, ExternalLink, Plus, RefreshCw, Save, Search, Trash2, X } from 'lucide-react'
import { absoluteApiV1Path, api, getApiErrorMessage } from '../lib/api'
import { groupManualItems, manualCategorySections, withManualCategory } from '../content/supportGuideManuals'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'

function unwrapData(res) {
  const payload = res?.data
  if (payload?.data !== undefined) return payload.data
  return payload ?? null
}

function isBlank(value) {
  return value == null || String(value).trim() === ''
}

function resolveManualLink(href) {
  if (!href) return ''
  const value = String(href).trim()
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  const path = value.startsWith('/') ? value.slice(1) : value
  if (path.startsWith('public/')) return absoluteApiV1Path(path)
  return value
}

function effectiveViewUrl(row) {
  if (!isBlank(row?.viewUrl)) return row.viewUrl
  if (row?.builtIn && !isBlank(row?.slug)) return `public/support-guide/manuals/by-slug/${row.slug}/html`
  return row?.effectiveViewUrl || ''
}

function effectiveDownloadUrl(row) {
  if (!isBlank(row?.downloadUrl)) return row.downloadUrl
  if (row?.builtIn && !isBlank(row?.slug)) return `public/support-guide/manuals/by-slug/${row.slug}/pdf`
  return row?.effectiveDownloadUrl || ''
}

function hasCustomLink(row) {
  return !isBlank(row?.viewUrl) || !isBlank(row?.downloadUrl)
}

function formatUpdatedAt(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).replace('T', ' ').slice(0, 16)
  return date.toLocaleString('ko-KR', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function linkModeLabel(row) {
  if (hasCustomLink(row)) return '외부 링크'
  if (row?.builtIn) return '내장'
  return '링크 필요'
}

function normalizeRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => withManualCategory({ ...row, id: row.slug || row.id }))
}

const emptyCreateForm = () => ({
  slug: '',
  title: '',
  summary: '',
  viewUrl: '',
  downloadUrl: '',
  sortOrder: '',
  published: true,
})

function CompactStat({ label, value, tone = 'slate' }) {
  const toneClass = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    sky: 'border-sky-200 bg-sky-50 text-sky-800',
    slate: 'border-slate-200 bg-white text-slate-700',
  }[tone]
  return (
    <div className={`inline-flex items-center gap-2 rounded border px-2.5 py-1.5 text-xs font-semibold ${toneClass}`}>
      <span>{label}</span>
      <strong className="text-sm tabular-nums">{value}</strong>
    </div>
  )
}

function Badge({ tone = 'slate', children }) {
  const toneClass = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    sky: 'border-sky-200 bg-sky-50 text-sky-700',
    slate: 'border-slate-200 bg-slate-50 text-slate-600',
  }[tone]
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-semibold ${toneClass}`}>
      {children}
    </span>
  )
}

function FieldLabel({ children }) {
  return <span className="mb-1 block text-xs font-medium text-slate-600">{children}</span>
}

function TextInput({ className = '', ...props }) {
  return (
    <input
      {...props}
      className={`w-full min-w-0 rounded border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-brand-sky focus:ring-1 focus:ring-sky-100 disabled:bg-slate-50 ${className}`}
    />
  )
}

function TextArea({ className = '', ...props }) {
  return (
    <textarea
      {...props}
      className={`w-full min-w-0 resize-y rounded border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-brand-sky focus:ring-1 focus:ring-sky-100 disabled:bg-slate-50 ${className}`}
    />
  )
}

function ManualActionLink({ href, icon: Icon, children }) {
  const resolved = resolveManualLink(href)
  if (!resolved) {
    return (
      <span className="inline-flex cursor-not-allowed items-center gap-1 rounded border border-dashed border-slate-200 bg-slate-50 px-2 py-1.5 text-xs font-semibold text-slate-400">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {children}
      </span>
    )
  }
  return (
    <a
      href={resolved}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 hover:border-sky-300 hover:bg-sky-50"
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {children}
    </a>
  )
}

function filterRows(rows, filters) {
  const query = filters.query.trim().toLowerCase()
  return rows.filter((row) => {
    if (query) {
      const text = [row.slug, row.title, row.summary, row.viewUrl, row.downloadUrl]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!text.includes(query)) return false
    }
    if (filters.category === 'other' && row.category !== 'other') return false
    if (filters.category !== 'all' && filters.category !== 'other' && row.category !== filters.category) return false
    if (filters.visibility === 'published' && !row.published) return false
    if (filters.visibility === 'hidden' && row.published) return false
    if (filters.linkMode === 'builtin' && (!row.builtIn || hasCustomLink(row))) return false
    if (filters.linkMode === 'custom' && !hasCustomLink(row)) return false
    if (filters.linkMode === 'missing' && (!isBlank(effectiveViewUrl(row)) || !isBlank(effectiveDownloadUrl(row)))) return false
    return true
  })
}

export default function OpsSupportGuideManualsPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [saveMsg, setSaveMsg] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [createForm, setCreateForm] = useState(emptyCreateForm)
  const [creating, setCreating] = useState(false)
  const [filters, setFilters] = useState({ query: '', category: 'all', visibility: 'all', linkMode: 'all' })

  const load = useCallback(async () => {
    setErr('')
    setSaveMsg('')
    setLoading(true)
    try {
      const res = await api.get('/ops/support-guide/manuals')
      const data = unwrapData(res)
      setRows(normalizeRows(data))
    } catch (e) {
      setErr(getApiErrorMessage(e, '매뉴얼 목록을 불러오지 못했습니다.'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const stats = useMemo(() => {
    const total = rows.length
    const published = rows.filter((row) => row.published).length
    const builtIn = rows.filter((row) => row.builtIn && !hasCustomLink(row)).length
    const missing = rows.filter((row) => isBlank(effectiveViewUrl(row)) && isBlank(effectiveDownloadUrl(row))).length
    return { total, published, builtIn, hidden: Math.max(0, total - published), missing }
  }, [rows])

  const filteredRows = useMemo(() => filterRows(rows, filters), [rows, filters])
  const groupedRows = useMemo(() => groupManualItems(filteredRows), [filteredRows])

  const updateRow = (id, field, value) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)))
  }

  const handleCreate = async () => {
    setErr('')
    setSaveMsg('')
    setCreating(true)
    try {
      const sortOrder =
        createForm.sortOrder === '' || createForm.sortOrder == null
          ? null
          : Number(createForm.sortOrder)
      const body = {
        slug: createForm.slug.trim(),
        title: createForm.title.trim(),
        summary: createForm.summary || '',
        viewUrl: createForm.viewUrl || '',
        downloadUrl: createForm.downloadUrl || '',
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : null,
        published: Boolean(createForm.published),
      }
      const res = await api.post('/ops/support-guide/manuals', body)
      const data = unwrapData(res)
      setRows(normalizeRows(data))
      setCreateForm(emptyCreateForm())
      setShowAdd(false)
      setSaveMsg('새 매뉴얼을 등록했습니다.')
    } catch (e) {
      setErr(getApiErrorMessage(e, '매뉴얼을 등록하지 못했습니다.'))
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (row) => {
    if (!window.confirm(`'${row.title || row.slug}' 매뉴얼을 삭제할까요? 공개 이용 가이드에서도 사라집니다.`)) return
    setErr('')
    setSaveMsg('')
    setSaving(true)
    try {
      const res = await api.delete(`/ops/support-guide/manuals/${row.id}`)
      const data = unwrapData(res)
      setRows(normalizeRows(data))
      setSaveMsg('매뉴얼을 삭제했습니다.')
    } catch (e) {
      setErr(getApiErrorMessage(e, '매뉴얼을 삭제하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    setErr('')
    setSaveMsg('')
    setSaving(true)
    try {
      const payload = rows.map((row) => {
        const sortOrder = Number(row.sortOrder)
        return {
          id: row.id,
          title: row.title || '',
          summary: row.summary ?? '',
          viewUrl: row.viewUrl ?? '',
          downloadUrl: row.downloadUrl ?? '',
          sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
          published: Boolean(row.published),
        }
      })
      const res = await api.put('/ops/support-guide/manuals', { items: payload })
      const data = unwrapData(res)
      setRows(normalizeRows(data))
      setSaveMsg('저장했습니다.')
    } catch (e) {
      setErr(getApiErrorMessage(e, '매뉴얼 변경사항을 저장하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="w-full max-w-none px-4 py-6 sm:px-6 lg:px-8">
        <Loading label="매뉴얼 목록을 불러오는 중…" />
      </div>
    )
  }

  return (
    <div className="w-full max-w-none space-y-4 px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm text-slate-500">운영자 · 기타관리</div>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">매뉴얼 관리</h1>
          <p className="mt-1 text-sm text-slate-600">
            공개 이용 가이드에 노출할 메뉴별 매뉴얼과 보기·PDF 링크를 관리합니다.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setShowAdd((value) => !value)
              setErr('')
              setSaveMsg('')
            }}
            className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            disabled={saving || creating}
          >
            {showAdd ? <X className="size-4" aria-hidden="true" /> : <Plus className="size-4" aria-hidden="true" />}
            {showAdd ? '등록 닫기' : '새 매뉴얼'}
          </button>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            disabled={saving || creating}
          >
            <RefreshCw className="size-4" aria-hidden="true" />
            새로고침
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="inline-flex items-center gap-1.5 rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90 disabled:opacity-60"
            disabled={saving || creating}
          >
            <Save className="size-4" aria-hidden="true" />
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <CompactStat label="전체" value={stats.total} />
        <CompactStat label="공개" value={stats.published} tone="emerald" />
        <CompactStat label="비공개" value={stats.hidden} />
        <CompactStat label="내장" value={stats.builtIn} tone="sky" />
        <CompactStat label="링크 필요" value={stats.missing} tone="amber" />
      </div>

      {err ? <InlineError message={err} className="rounded border border-red-100 bg-red-50" /> : null}
      {saveMsg ? <div className="text-sm font-medium text-emerald-700">{saveMsg}</div> : null}

      {showAdd ? (
        <section className="rounded border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-slate-900">새 매뉴얼 등록</h2>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                className="size-4 rounded border-slate-300"
                checked={Boolean(createForm.published)}
                onChange={(e) => setCreateForm((form) => ({ ...form, published: e.target.checked }))}
              />
              공개
            </label>
          </div>
          <div className="grid gap-3 lg:grid-cols-12">
            <label className="block lg:col-span-2">
              <FieldLabel>식별명 *</FieldLabel>
              <TextInput
                value={createForm.slug}
                onChange={(e) => setCreateForm((form) => ({ ...form, slug: e.target.value }))}
                placeholder="requests"
                className="font-mono"
              />
            </label>
            <label className="block lg:col-span-3">
              <FieldLabel>제목 *</FieldLabel>
              <TextInput
                value={createForm.title}
                onChange={(e) => setCreateForm((form) => ({ ...form, title: e.target.value }))}
              />
            </label>
            <label className="block lg:col-span-1">
              <FieldLabel>순서</FieldLabel>
              <TextInput
                type="number"
                value={createForm.sortOrder}
                onChange={(e) => setCreateForm((form) => ({ ...form, sortOrder: e.target.value }))}
                placeholder="자동"
                className="tabular-nums"
              />
            </label>
            <label className="block lg:col-span-6">
              <FieldLabel>공개 설명</FieldLabel>
              <TextInput
                value={createForm.summary}
                onChange={(e) => setCreateForm((form) => ({ ...form, summary: e.target.value }))}
                placeholder="공개 이용 가이드에 표시할 설명"
              />
            </label>
            <label className="block lg:col-span-6">
              <FieldLabel>보기 URL</FieldLabel>
              <TextInput
                value={createForm.viewUrl}
                onChange={(e) => setCreateForm((form) => ({ ...form, viewUrl: e.target.value }))}
                placeholder="비워두면 내장 HTML 사용 가능"
                className="font-mono text-xs"
              />
            </label>
            <label className="block lg:col-span-6">
              <FieldLabel>PDF URL</FieldLabel>
              <TextInput
                value={createForm.downloadUrl}
                onChange={(e) => setCreateForm((form) => ({ ...form, downloadUrl: e.target.value }))}
                placeholder="비워두면 내장 PDF 사용 가능"
                className="font-mono text-xs"
              />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating || !createForm.slug.trim() || !createForm.title.trim()}
              className="inline-flex items-center gap-1.5 rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90 disabled:opacity-50"
            >
              <Plus className="size-4" aria-hidden="true" />
              {creating ? '등록 중…' : '등록'}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreateForm(emptyCreateForm())
                setShowAdd(false)
              }}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              취소
            </button>
          </div>
        </section>
      ) : null}

      <section className="rounded border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 p-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_8.5rem_9rem_9rem] lg:w-[54rem]">
            <label className="relative block">
              <span className="sr-only">매뉴얼 검색</span>
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <TextInput
                value={filters.query}
                onChange={(e) => setFilters((prev) => ({ ...prev, query: e.target.value }))}
                placeholder="제목, 식별명, URL 검색"
                className="pl-8"
              />
            </label>
            <select
              value={filters.category}
              onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}
              className="rounded border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700"
              aria-label="카테고리 필터"
            >
              <option value="all">카테고리 전체</option>
              {manualCategorySections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.label}
                </option>
              ))}
            </select>
            <select
              value={filters.visibility}
              onChange={(e) => setFilters((prev) => ({ ...prev, visibility: e.target.value }))}
              className="rounded border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700"
              aria-label="공개 상태 필터"
            >
              <option value="all">공개 전체</option>
              <option value="published">공개</option>
              <option value="hidden">비공개</option>
            </select>
            <select
              value={filters.linkMode}
              onChange={(e) => setFilters((prev) => ({ ...prev, linkMode: e.target.value }))}
              className="rounded border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700"
              aria-label="링크 유형 필터"
            >
              <option value="all">링크 전체</option>
              <option value="builtin">내장</option>
              <option value="custom">외부</option>
              <option value="missing">필요</option>
            </select>
          </div>
          <div className="text-xs text-slate-500">
            {filteredRows.length.toLocaleString()} / {rows.length.toLocaleString()}개 표시
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">등록된 매뉴얼이 없습니다.</div>
        ) : filteredRows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">조건에 맞는 매뉴얼이 없습니다.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {groupedRows.map((section) => (
              <section key={section.id}>
                <div className="bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600">{section.label}</div>
                {section.items.map((row) => {
                  const viewHref = effectiveViewUrl(row)
                  const downloadHref = effectiveDownloadUrl(row)
                  return (
                <article key={row.id} className="border-t border-slate-100 p-3 hover:bg-slate-50/60">
                  <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1.35fr)_minmax(0,1.25fr)_8.5rem_7.5rem]">
                    <div className="min-w-0">
                      <FieldLabel>매뉴얼</FieldLabel>
                      <TextInput
                        aria-label={`${row.slug} 제목`}
                        value={row.title || ''}
                        onChange={(e) => updateRow(row.id, 'title', e.target.value)}
                        className="font-semibold"
                      />
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-500">
                          {row.slug}
                        </span>
                        <Badge>{row.categoryLabel}</Badge>
                        <Badge tone={row.published ? 'emerald' : 'slate'}>{row.published ? '공개' : '비공개'}</Badge>
                        <Badge tone={row.builtIn ? 'sky' : 'amber'}>{linkModeLabel(row)}</Badge>
                        <span className="text-[11px] text-slate-400">수정 {formatUpdatedAt(row.updatedAt)}</span>
                      </div>
                    </div>

                    <label className="min-w-0">
                      <FieldLabel>공개 설명</FieldLabel>
                      <TextArea
                        aria-label={`${row.slug} 공개 설명`}
                        rows={2}
                        value={row.summary || ''}
                        onChange={(e) => updateRow(row.id, 'summary', e.target.value)}
                      />
                    </label>

                    <div className="min-w-0 space-y-2">
                      <label className="block min-w-0">
                        <FieldLabel>보기 URL</FieldLabel>
                        <TextInput
                          aria-label={`${row.slug} 보기 URL`}
                          value={row.viewUrl || ''}
                          onChange={(e) => updateRow(row.id, 'viewUrl', e.target.value)}
                          placeholder={row.builtIn ? '비우면 내장 HTML' : 'https://…'}
                          className="font-mono text-xs"
                        />
                      </label>
                      <label className="block min-w-0">
                        <FieldLabel>PDF URL</FieldLabel>
                        <TextInput
                          aria-label={`${row.slug} PDF URL`}
                          value={row.downloadUrl || ''}
                          onChange={(e) => updateRow(row.id, 'downloadUrl', e.target.value)}
                          placeholder={row.builtIn ? '비우면 내장 PDF' : 'https://…'}
                          className="font-mono text-xs"
                        />
                      </label>
                    </div>

                    <div className="min-w-0">
                      <FieldLabel>상태</FieldLabel>
                      <div className="flex flex-wrap items-center gap-2 xl:block xl:space-y-2">
                        <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                          <input
                            type="checkbox"
                            className="size-4 rounded border-slate-300"
                            checked={Boolean(row.published)}
                            onChange={(e) => updateRow(row.id, 'published', e.target.checked)}
                          />
                          공개
                        </label>
                        <TextInput
                          type="number"
                          aria-label={`${row.slug} 순서`}
                          value={row.sortOrder ?? 0}
                          onChange={(e) => updateRow(row.id, 'sortOrder', Number(e.target.value))}
                          className="w-24 tabular-nums"
                        />
                      </div>
                    </div>

                    <div className="min-w-0">
                      <FieldLabel>작업</FieldLabel>
                      <div className="flex flex-wrap gap-1.5 xl:flex-col">
                        <ManualActionLink href={viewHref} icon={ExternalLink}>
                          보기
                        </ManualActionLink>
                        <ManualActionLink href={downloadHref} icon={Download}>
                          PDF
                        </ManualActionLink>
                        <button
                          type="button"
                          onClick={() => handleDelete(row)}
                          className="inline-flex items-center gap-1 rounded border border-rose-200 bg-white px-2 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                          disabled={saving || creating}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
                  )
                })}
              </section>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
